import {
  LinkedinAccount,
  LinkedinAccountStatuses,
  normalizeAccountStatus,
} from 'features/co-pilot/linkedin-accounts/linkedin-accounts.types';

/** Statuses that need a person; mapped to the same action labels the account list shows. */
const ACTION_KEYS: Partial<Record<string, string>> = {
  [LinkedinAccountStatuses.TWO_FA]: 'enter2fa',
  [LinkedinAccountStatuses.WRONG_PIN]: 'requestNewCode',
  [LinkedinAccountStatuses.WRONG_CREDENTIALS]: 'correctAccessData',
  [LinkedinAccountStatuses.CAPTCHA]: 'solveCaptcha',
  [LinkedinAccountStatuses.TIMED_OUT_CAPTCHA]: 'requestNewCaptcha',
  [LinkedinAccountStatuses.RECRUITER_PARALLEL_LOGIN]: 'parallelLogin',
  [LinkedinAccountStatuses.CHOOSE_CONTRACT]: 'chooseContract',
  [LinkedinAccountStatuses.ONBOARDING_INCOMPLETE]: 'incompleteOnboarding',
  [LinkedinAccountStatuses.THIRD_PARTY]: 'thirdPartyLogin',
  [LinkedinAccountStatuses.THIRD_PARTY_TEXT]: 'thirdPartyLogin',
  [LinkedinAccountStatuses.TWO_FA_NOT_ENABLED]: 'twoFaNotEnabled',
};

export interface AccountProblem {
  accountId: string;
  name: string;
  label: string;
  /** What the backend wrote into statusMessage — the concrete error, if there is one. */
  detail?: string;
}

/** statusMessage is often JSON ({ message, userAction }) — show the human part. */
const readStatusMessage = (raw?: string): string | undefined => {
  if (!raw) return undefined;
  try {
    const parsed = JSON.parse(raw);
    const text = [parsed?.message, parsed?.userAction].filter((part) => typeof part === 'string' && part.trim()).join(' — ');
    return text || undefined;
  } catch {
    return raw;
  }
};

/**
 * Was ist an diesem Konto nicht sauber? `null` = verbunden oder gerade am Verbinden.
 * Ein verbundenes Konto, dessen Sales-Navigator-/Recruiter-Zugang nicht antwortet, zählt mit:
 * die normale Sitzung läuft, Kampagnen über den Sitz aber nicht.
 */
export const getAccountProblem = (account: LinkedinAccount, t: (key: string) => string): AccountProblem | null => {
  const status = normalizeAccountStatus(account.status);
  const name = [account.firstName, account.lastName].filter(Boolean).join(' ') || account.userEmail;
  const readiness = account.automationReadiness;
  const readinessMessage = readiness && readiness.state !== 'ready' ? readiness.message : undefined;
  const base = { accountId: account._id, name, detail: readStatusMessage(account.statusMessage) || readinessMessage };

  if (status === LinkedinAccountStatuses.SYNCHRONIZED) {
    if (account.seatAccess?.ok === false) {
      const seatKey = account.seatAccess.product === 'recruiter' ? 'recruiterSeatNotAnswering' : 'salesNavSeatNotAnswering';
      return { ...base, label: t(seatKey), detail: undefined };
    }
    return null;
  }
  if (status === LinkedinAccountStatuses.PENDING) return null;

  if (status === LinkedinAccountStatuses.NEEDS_MANUAL_LOGIN) return { ...base, label: t('accountNeedsManualLogin') };
  if (status === LinkedinAccountStatuses.TWO_FA_TIMED_OUT) return { ...base, label: t('twoFaTimedOut') };
  if (status === LinkedinAccountStatuses.NOT_SYNCHRONIZED) return { ...base, label: t('accountLoggedOut') };
  if (status === LinkedinAccountStatuses.FAIR_USE_PAUSE) return { ...base, label: t('accountTemporarilyRestricted') };

  const actionKey = ACTION_KEYS[status];
  return { ...base, label: actionKey ? `${t('accountAttentionRequired')}: ${t(actionKey)}` : status };
};
